import type { ConflictV2 } from "@classroom/domain";
import { useEffect, useState } from "react";
import { useTranslation } from "../../i18n";
import type { LocalStoreClient } from "../localStore/client";
import { syncMessage } from "./syncMessage";

export function ConflictChoices({
  client,
  workspaceId,
  conflicts,
  onResolved,
}: {
  client: LocalStoreClient;
  workspaceId: string;
  conflicts: ConflictV2[];
  onResolved: () => Promise<unknown>;
}) {
  const { t } = useTranslation("syncReview");
  const [labels, setLabels] = useState<Record<string, { title: string }>>({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    void Promise.all(conflicts.map(async (c) => [c.key, await client.syncConflictLabel(workspaceId, c.key)] as const))
      .then((values) => {
        if (!cancelled) setLabels(Object.fromEntries(values));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [client, workspaceId, conflicts]);
  const choose = async (decision: ConflictV2, operationId: string) => {
    if (busy) return;
    setBusy(decision.key);
    setError("");
    try {
      await client.syncResolve(workspaceId, decision, operationId);
    } catch (e) {
      setError(t(syncMessage(e instanceof Error ? e.message : "")));
    } finally {
      await onResolved();
      setBusy("");
    }
  };
  return (
    <section className="sync-conflicts" aria-label={t("conflict")}>
      <h2>{t("conflict")}</h2>
      <p>{t("conflictHint")}</p>
      {error && <p role="alert">{error}</p>}
      {conflicts.map((conflict, index) => (
        <article key={conflict.key}>
          <h3>{labels[conflict.key]?.title || t("changedRecord", { number: index + 1 })}</h3>
          <div className="sync-version-grid">
            {conflict.versions.map((version, i) => (
              <section key={version.operationId}>
                <h4>{t("version", { number: i + 1 })}</h4>
                <VersionValue value={version.value} />
                <button type="button" disabled={!!busy} onClick={() => void choose(conflict, version.operationId)}>
                  {t("chooseVersion")}
                </button>
              </section>
            ))}
          </div>
        </article>
      ))}
    </section>
  );
}
function VersionValue({ value }: { value: unknown }) {
  const { t } = useTranslation("syncReview");
  if (value === null || value === false) return <p>{t("deletedVersion")}</p>;
  if (value === true) return <p>{t("keptVersion")}</p>;
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return (
      <>
        <p>{String(record.title ?? record.name ?? record.date ?? "")}</p>
        {typeof record.plainText === "string" ? (
          <pre>{record.plainText}</pre>
        ) : (
          <dl>
            {Object.entries(record)
              .filter(
                ([key]) =>
                  !["id", "workspaceId", "content", "revision", "updatedAt", "lastOperationId", "plainText"].includes(
                    key,
                  ),
              )
              .map(([key, v]) => (
                <div key={key}>
                  <dt>{key}</dt>
                  <dd>{typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
                </div>
              ))}
          </dl>
        )}
        {record.deletedAt && <p>{t("deletedVersion")}</p>}
      </>
    );
  }
  return <pre>{String(value)}</pre>;
}
